import { useEffect, useId, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Plus, X } from 'lucide-react';
import { Botao } from '../../componentes/base';
import { EntradaComMascara, EntradaSensivel, Formulario, mascaraDocumento, Rotulo, Selecao, useCamposPersonalizados, type DefCampo } from '../../componentes/formulario';
import { hojeNoFuso } from '../../componentes/periodo/periodo';
import { Drawer, Modal } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { api, del, ErroRequisicao, mensagemDeErro, patch, post } from '../../nucleo/api';
import { CriarCamposDica } from '../../nucleo/criar-campo';
import { mascaraTelefoneDigitando, nomePessoa } from '../../nucleo/formato';
import { EXEMPLOS_MASCARA } from '../../nucleo/mascaras';
import { useSessao } from '../../nucleo/sessao';
import { BASES_LEGAIS, corpoDosResponsaveis, EXTRAS_FORA_DO_CRM, RESPONSAVEL_TODOS, responsaveisDoRegistro, SelecaoMultipla, SEXOS, TIPOS_TELEFONE, useMembros, useSetores, type ParceiroCrm, type TipoMeio } from './crm-comum';
import { consultar, enderecoDaConsulta, estadoDaResposta, fiscalDaConsulta, regimeDaConsulta, type EstadoConsulta } from './consulta';
import { REGIMES } from './FiscalParceiro';
import type { Ficha as FichaParceiro, Fiscal } from './tipos';

/**
 * CRM no novo desenho (lote 1): "Criar Empresa" e "Criar Contato" do clone do RD, sobre o cadastro único (`parceiros.criar`
 * e `parceiros.editar`). Os campos personalizados do workspace (WhatsApp, LinkedIn...) entram pelo `Formulario`.
 *
 * Ajustes de 27/10 (trilha cadastros): o contato ganha vários telefones (com tipo) e e-mails (C5), o usuário do WhatsApp, o
 * LinkedIn e a data de aniversário no lugar certo (C4, C5; campos personalizados); a empresa, vários segmentos com cadastro
 * na hora, vários responsáveis e o endereço (C13). O principal segue nas colunas; os adicionais vão por
 * `PUT /cadastros/parceiros/:id/adicionais` (com `cadastros.editar`).
 */
const texto = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim() : null);

async function gravar(atual: ParceiroCrm | null | undefined, corpo: Record<string, unknown>): Promise<string> {
  if (atual) {
    await patch(`/cadastros/parceiros/${atual.id}`, corpo);
    return atual.id;
  }
  return (await post<{ id: string }>('/cadastros/parceiros', corpo)).id;
}

function useDepoisDeSalvar() {
  const qc = useQueryClient();
  const toast = useToast();
  const { t: tn } = useTranslation();
  return async () => {
    await qc.invalidateQueries({ queryKey: ['/cadastros/parceiros'] });
    await qc.invalidateQueries({ queryKey: ['crm'] });
    toast.sucesso(tn('comum.salvo'));
  };
}

interface Meio {
  chave: string;
  tipo: TipoMeio;
  valor: string;
}
const novoMeio = (tipo: TipoMeio, valor = ''): Meio => ({ chave: crypto.randomUUID(), tipo, valor });
/** Os telefones ou e-mails adicionais do registro, no formato da lista do formulário (telefone já com a máscara). */
/** rl0211-10: o servidor guarda em E.164; o campo mostra o número nacional formatado (ao salvar, o servidor recoloca o +55). */
const telefoneNoCampo = (v: string) => mascaraTelefoneDigitando(/^\+55\d{10,11}$/.test(v) ? v.slice(3) : v);

const meiosDe = (atual: ParceiroCrm | null | undefined, m: 'telefone' | 'email') => (atual?.meios ?? []).filter((x) => x.meio === m).map((x) => novoMeio(x.tipo, m === 'telefone' ? telefoneNoCampo(x.valor) : x.valor));
/** Lista de 02/11 (itens 1 e 3): o tipo do telefone principal (padrão Comercial, como no RD); registro sem tipo mostra o padrão. */
const tipoDoTelefone = (atual: ParceiroCrm | null | undefined): TipoMeio => atual?.telefone_tipo ?? 'comercial';
/** O tipo do telefone principal, ao lado do número, igual ao dos adicionais (`ListaMeios`). */
function TipoDoTelefone({ valor, aoMudar }: { valor: TipoMeio; aoMudar: (t: TipoMeio) => void }) {
  const { t } = useTranslation('cadastros');
  return <Selecao aria-label={t('crm.aj.tipoTelefone', { n: 1 })} value={valor} onChange={(e) => aoMudar(e.target.value as TipoMeio)} opcoes={TIPOS_TELEFONE.map((x) => ({ valor: x, rotulo: t(`crm.aj.tiposTelefone.${x}`) }))} data-tipo-telefone="" />;
}
/** Os telefones ou e-mails adicionais no corpo do `PUT /adicionais` (vazios saem). */
const listaDeMeios = (l: Meio[]) => l.filter((m) => m.valor.trim()).map((m) => ({ tipo: m.tipo, valor: m.valor.trim() }));

/**
 * Os telefones ou e-mails adicionais (C5), abaixo do principal: tipo e número (só telefone), remover e "Adicionar". Os campos
 * mascaram sozinhos no modo discreto (`EntradaSensivel`). O erro do servidor aponta o item (`telefones.2`).
 */
function ListaMeios({ meio, itens, aoMudar, erros, pode }: { meio: 'telefone' | 'email'; itens: Meio[]; aoMudar: (m: Meio[]) => void; erros: Record<string, string>; pode: boolean }) {
  const { t } = useTranslation('cadastros');
  const id = useId();
  const mudar = (chave: string, d: Partial<Meio>) => aoMudar(itens.map((m) => (m.chave === chave ? { ...m, ...d } : m)));
  const rotulo = meio === 'telefone' ? t('crm.campos.telefone') : t('crm.campos.email');
  return (
    <div className="flex flex-col gap-2" data-lista-meios={meio}>
      {itens.map((m, i) => {
        const erro = erros[`${meio}s.${i}`];
        return (
          <div key={m.chave} className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              {meio === 'telefone' && (
                <div className="w-36 shrink-0">
                  <Selecao aria-label={t('crm.aj.tipoTelefone', { n: i + 2 })} value={m.tipo} onChange={(e) => mudar(m.chave, { tipo: e.target.value as TipoMeio })} opcoes={TIPOS_TELEFONE.map((x) => ({ valor: x, rotulo: t(`crm.aj.tiposTelefone.${x}`) }))} />
                </div>
              )}
              {meio === 'telefone' ? (
                <EntradaComMascara
                  id={`${id}-${i}`}
                  mascara={meio}
                  rotulo={`${rotulo} ${i + 2}`}
                  aria-label={`${rotulo} ${i + 2}`}
                  aria-invalid={!!erro}
                  type="tel"
                  inputMode="tel"
                  placeholder={EXEMPLOS_MASCARA.telefone}
                  formato={mascaraTelefoneDigitando}
                  valor={m.valor}
                  aoMudarTexto={(valor) => mudar(m.chave, { valor })}
                />
              ) : (
                <EntradaSensivel
                  id={`${id}-${i}`}
                  mascara={meio}
                  rotulo={`${rotulo} ${i + 2}`}
                  aria-label={`${rotulo} ${i + 2}`}
                  aria-invalid={!!erro}
                  type="email"
                  placeholder="seunome@email.com"
                  value={m.valor}
                  onChange={(e) => mudar(m.chave, { valor: e.target.value })}
                />
              )}
              <Botao variante="fantasma" tamanho="sm" aria-label={t('crm.aj.removerMeio', { campo: `${rotulo} ${i + 2}` })} title={t('crm.aj.remover')} icone={<X className="size-4" />} onClick={() => aoMudar(itens.filter((x) => x.chave !== m.chave))} />
            </div>
            {erro && <p className="text-xs text-perigo">{erro}</p>}
          </div>
        );
      })}
      {pode && itens.length < 10 && (
        <Botao variante="fantasma" tamanho="sm" className="self-start" icone={<Plus className="size-4" />} onClick={() => aoMudar([...itens, novoMeio(meio === 'telefone' ? 'celular' : 'comercial')])} data-adicionar-meio={meio}>
          {meio === 'telefone' ? t('crm.aj.adicionarTelefone') : t('crm.aj.adicionarEmail')}
        </Botao>
      )}
    </div>
  );
}

/**
 * Grava os adicionais e devolve os erros por item (o `Formulario` não conhece os itens da lista). Na criação o registro já
 * existe: a falha vira aviso (o cadastro fica, sem os adicionais) em vez de deixar o formulário aberto para criar de novo.
 */
async function gravarAdicionais(id: string, corpo: Record<string, unknown>, setErros: (e: Record<string, string>) => void, criando: boolean, avisar: (m: string) => void) {
  try {
    await api('PUT', `/cadastros/parceiros/${id}/adicionais`, corpo);
    setErros({});
  } catch (e) {
    setErros(e instanceof ErroRequisicao ? e.porCampo() : {});
    if (!criando) throw e;
    avisar(mensagemDeErro(e));
  }
}

/** Cadastro de segmento na hora (C13), pelo mesmo `POST /cadastros/setores` da tela de classificação. */
function NovoSegmento({ aberto, aoFechar, aoCriar }: { aberto: boolean; aoFechar: () => void; aoCriar: (id: string) => void }) {
  const { t } = useTranslation('cadastros');
  const { t: tn } = useTranslation();
  const qc = useQueryClient();
  const id = useId();
  const [nome, setNome] = useState('');
  const [erro, setErro] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const fechar = () => {
    setNome('');
    setErro('');
    aoFechar();
  };
  const criar = async () => {
    if (!nome.trim()) return setErro(tn('erros.obrigatorio'));
    setOcupado(true);
    try {
      const novo = crypto.randomUUID();
      await post('/cadastros/setores', { id: novo, nome: nome.trim() });
      await qc.invalidateQueries({ queryKey: ['cadastros', 'setores'] });
      aoCriar(novo);
      fechar();
    } catch (e) {
      setErro(mensagemDeErro(e));
    } finally {
      setOcupado(false);
    }
  };
  return (
    <Modal
      aberto={aberto}
      aoFechar={fechar}
      titulo={t('crm.aj.novoSegmento')}
      rodape={
        <>
          <Botao variante="fantasma" onClick={fechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao variante="primario" carregando={ocupado} onClick={() => void criar()} data-criar-segmento="">
            {t('crm.aj.criarSegmento')}
          </Botao>
        </>
      }
    >
      <Rotulo id={id} rotulo={t('crm.campos.segmento')} obrigatorio erro={erro || undefined}>
        <input
          id={id}
          maxLength={60}
          value={nome}
          onChange={(e) => setNome(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void criar();
            }
          }}
          className="h-11 rounded-lg border border-borda-campo bg-campo px-3 text-sm text-tinta-1 md:h-10"
        />
      </Rotulo>
    </Modal>
  );
}

const COLUNAS_FISCAL = ['razao_social', 'nome_fantasia', 'data_abertura', 'porte', 'natureza_juridica', 'situacao_cadastral', 'situacao_data', 'capital_social_centavos', 'isento_ie', 'inscricao_estadual', 'inscricao_municipal', 'produtor_rural', 'inscricoes_produtor'] as const;

/**
 * Lista de 02/11 (item 3): o corpo do `PUT /cadastros/parceiros/:id/fiscal` pela razão social e pelo Nome Fantasia do pop-up da
 * empresa, ou nada quando não há o que gravar. O `PUT` troca os dados fiscais inteiros: com cadastro fiscal já feito, vão junto os
 * que existem (porte, inscrições, capital...); a Receita só entra na criação (a consulta nunca sobrescreve o que já está gravado).
 * O `nome` segue sendo o nome que o sistema inteiro lê; o fantasia fiscal o acompanha (se estavam iguais ou se ainda não havia).
 */
export function corpoFiscalDaEmpresa(a: { fiscal: Fiscal | null | undefined; nomeAtual: string | null; razao: string | null; nome: string; receita: Partial<Fiscal> | null }): Partial<Fiscal> | null {
  const { fiscal, receita } = a;
  if (fiscal) {
    const acompanha = !fiscal.nome_fantasia || fiscal.nome_fantasia === a.nomeAtual;
    const mudouRazao = a.razao !== (fiscal.razao_social ?? null);
    const mudouFantasia = !!fiscal.nome_fantasia && acompanha && a.nome !== fiscal.nome_fantasia;
    if (!mudouRazao && !mudouFantasia) return null;
    return { ...Object.fromEntries(COLUNAS_FISCAL.map((k) => [k, fiscal[k]])), razao_social: a.razao, nome_fantasia: acompanha ? a.nome : fiscal.nome_fantasia };
  }
  const daReceita = receita && Object.keys(receita).length ? receita : null;
  if (!a.razao && !daReceita) return null;
  return { ...(daReceita ?? {}), razao_social: a.razao, nome_fantasia: a.nome };
}

const CAMPOS_ENDERECO = ['cep', 'logradouro', 'numero', 'complemento', 'bairro', 'cidade', 'uf'] as const;

type AtualEmpresa = ParceiroCrm & Partial<Pick<FichaParceiro, 'enderecos' | 'regimes' | 'fiscal'>>;

/** `criarOutra` (lista de 03/11, item 4): mostra "Salvar e criar outra" na criação; quem precisa do registro criado não liga. */
type PropsFormEmpresa = { aberto: boolean; aoFechar: () => void; atual?: AtualEmpresa | null; aoSalvar?: (id: string) => void; criarOutra?: boolean };

/**
 * Lista de 02/11 (ROB-M1): o estado fora do `Formulario` (tipo do telefone, meios, responsáveis, segmentos, consultas) só existe
 * com a gaveta aberta. Cada abertura monta o formulário de novo a partir do `atual` vigente: "criar de novo", Cancelar e lista
 * velha recomeçam sem depender de cada chamador.
 */
export function FormEmpresa(props: PropsFormEmpresa) {
  // Lista de 03/11, item 4: "Salvar e criar outra" recomeça o formulário inteiro (a chave troca) e segue aberto.
  const [n, setN] = useState(0);
  return props.aberto ? <FormEmpresaAberto key={n} {...props} aoRecomecar={() => setN((x) => x + 1)} /> : null;
}

function FormEmpresaAberto({ aberto, aoFechar, atual, aoSalvar, criarOutra, aoRecomecar }: PropsFormEmpresa & { aoRecomecar: () => void }) {
  const outra = useRef(false);
  const { t } = useTranslation('cadastros');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const setores = useSetores();
  const membros = useMembros();
  const depois = useDepoisDeSalvar();
  const toast = useToast();
  const idSeg = useId();
  const idResp = useId();
  const extrasDefs = useCamposPersonalizados('cadastros.parceiro');
  const podeEditar = s.pode('cadastros.editar');
  const inicialSetores = atual ? [...(atual.setor_id ? [atual.setor_id] : []), ...(atual.setores_adicionais ?? []).map((x) => x.id)] : [];
  // Lista de 30/10 (B), item 3: na criação, "Todos" (empresa sem carteira separada) e a própria pessoa já marcados. O "Todos"
  // grava pelos adicionais (`cadastros.editar`): sem essa permissão, a opção não aparece.
  const inicialResp = atual ? responsaveisDoRegistro(atual) : [...(podeEditar ? [RESPONSAVEL_TODOS] : []), s.usuario.id];
  const [segs, setSegs] = useState<string[]>(inicialSetores);
  const [resps, setResps] = useState<string[]>(inicialResp);
  const [novoSeg, setNovoSeg] = useState(false);
  // Lista de 02/11 (item 3): o telefone com tipo (principal e adicionais) e os e-mails adicionais, como no contato.
  const [tipoTel, setTipoTel] = useState<TipoMeio>(() => tipoDoTelefone(atual));
  const [telefones, setTelefones] = useState<Meio[]>(() => meiosDe(atual, 'telefone'));
  const [emails, setEmails] = useState<Meio[]>(() => meiosDe(atual, 'email'));
  const [errosMeios, setErrosMeios] = useState<Record<string, string>>({});
  const [chave, setChave] = useState(atual?.id ?? 'nova');
  // Reabrir para outro registro (ou criar de novo) recomeça os valores fora do `Formulario`.
  if ((atual?.id ?? 'nova') !== chave) {
    setChave(atual?.id ?? 'nova');
    setSegs(inicialSetores);
    setResps(inicialResp);
    setTipoTel(tipoDoTelefone(atual));
    setTelefones(meiosDe(atual, 'telefone'));
    setEmails(meiosDe(atual, 'email'));
    setErrosMeios({});
  }
  // RCRM5-ROB-M1: o endereço criado nesta abertura (id gerado uma vez). Se o PATCH do cadastro cai depois do POST, o novo
  // "Salvar" corrige esse endereço em vez de criar outro; um 409 do mesmo id (resposta perdida) conta como criado.
  const enderecoNovo = useRef<{ id: string; criado: Record<string, string | null> | null } | null>(null);
  // Lista de 30/10 (B), item 3: o que a consulta do CNPJ trouxe nesta abertura (dados fiscais e o regime com a data da opção).
  const daReceita = useRef<{ fiscal: Record<string, unknown> | null; regime: { regime: string; inicio: string } | null }>({ fiscal: null, regime: null });
  useEffect(() => {
    if (aberto) {
      enderecoNovo.current = null;
      daReceita.current = { fiscal: null, regime: null };
    }
  }, [aberto, atual?.id]);
  type EnderecoBase = { id: string } & Partial<Record<(typeof CAMPOS_ENDERECO)[number], string | null>>;
  const enderecoAtual: EnderecoBase | undefined = atual?.enderecos?.[0];
  const grupoEndereco = t('crm.campos.endereco');
  // crm5 (E3): o CEP completo consulta o endereço (BrasilAPI pelo servidor, `cadastros.ver`) e preenche só os campos vazios.
  const [consultaCep, setConsultaCep] = useState<EstadoConsulta | null>(null);
  const preencherCep = s.pode('cadastros.ver')
    ? async (cep: string) => {
        setConsultaCep('cepBuscando');
        const r = await consultar('cep', cep);
        setConsultaCep(estadoDaResposta(r, 'cep'));
        if (r.situacao !== 'encontrado' || !r.dados) return null;
        const { 'endereco.cep': _cep, ...resto } = enderecoDaConsulta(r.dados, 'endereco.');
        void _cep;
        return resto;
      }
    : undefined;
  // Lista de 30/10 (B), item 3: o CNPJ é o primeiro campo; completo e válido, consulta a Receita pela mesma rota do cadastro
  // completo e preenche SÓ os campos vazios (o `Formulario` nunca sobrescreve o digitado; a ajuda do campo avisa). Nome da
  // empresa (o fantasia, senão a razão social), e-mail, endereço, a Data de fundação (abertura) e o Regime tributário.
  const [consultaCnpj, setConsultaCnpj] = useState<EstadoConsulta | null>(null);
  const temFundacao = extrasDefs.some((d) => d.nome === 'extras.data_fundacao');
  const preencherCnpj = s.pode('cadastros.ver')
    ? async (cnpj: string) => {
        setConsultaCnpj('buscando');
        const r = await consultar('cnpj', cnpj);
        setConsultaCnpj(estadoDaResposta(r, 'cnpj'));
        if (r.situacao !== 'encontrado' || !r.dados) return null;
        const d = r.dados;
        const regime = regimeDaConsulta(d);
        daReceita.current = { fiscal: fiscalDaConsulta(d) as Record<string, unknown>, regime };
        const texto = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim() : undefined);
        // Lista de 02/11 (item 3): preenche o Nome Fantasia e a razão social (só os vazios); sem fantasia na Receita, o Nome Fantasia usa a razão social.
        return {
          nome: texto(d.nome_fantasia) ?? texto(d.razao_social),
          razao_social: texto(d.razao_social),
          email: texto(d.email),
          ...enderecoDaConsulta(d.endereco as Record<string, unknown>, 'endereco.'),
          ...(temFundacao && texto(d.data_abertura) ? { 'extras.data_fundacao': d.data_abertura } : {}),
          ...(regime && podeEditar ? { regime: regime.regime } : {}),
        };
      }
    : undefined;
  // Lista de 30/10 (B), item 3.1 (d): o Regime tributário no formulário, nos tipos e na ordem do sistema (rlb-1 SPEC-B4: MEI,
  // Simples Nacional, Lucro presumido, Lucro real). Grava como período novo (`POST .../regimes`, com `cadastros.editar`); sem
  // ela o campo aparece só para leitura (o regime vigente, na edição).
  const regimeVigente = (atual?.regimes ?? []).find((r) => r.vigente) ?? null;
  const campos: DefCampo[] = [
    {
      nome: 'documento',
      rotulo: t('campos.cnpj'),
      tipo: 'documento',
      documento: 'cnpj',
      preencher: preencherCnpj,
      ajuda: consultaCnpj ? t(`consulta.${consultaCnpj}`) : undefined,
    },
    // Lista de 02/11 (item 3): a razão social logo abaixo do CNPJ (grava nos dados fiscais, como o cadastro completo; só com `cadastros.editar`).
    { nome: 'razao_social', rotulo: t('crm.campos.razaoSocial'), tipo: 'texto', sensivel: 'nome', desabilitado: !podeEditar, ajuda: !podeEditar ? t('crm.campos.razaoSocialSoLeitura') : undefined, placeholder: t('crm.campos.razaoSocial') },
    // O Nome Fantasia é o `nome` do cadastro (o que o sistema inteiro lê).
    { nome: 'nome', rotulo: t('crm.campos.nomeFantasia'), tipo: 'texto', sensivel: 'nome', obrigatorio: true, placeholder: t('crm.campos.nomeFantasia') },
    { nome: 'site', rotulo: t('crm.campos.site'), tipo: 'texto', placeholder: 'https://' },
    // crm5 (E2): o e-mail da empresa (a coluna `email` do cadastro, a mesma do cadastro completo); os adicionais vêm abaixo.
    { nome: 'email', rotulo: t('crm.campos.email'), tipo: 'email', placeholder: 'contato@empresa.com' },
    // Lista de 02/11 (item 3): o telefone com tipo no lugar do WhatsApp (que virou tipo de telefone), como no contato.
    { nome: 'telefone', rotulo: t('crm.campos.telefone'), tipo: 'telefone', transformar: mascaraTelefoneDigitando, ladoEsquerdo: <TipoDoTelefone valor={tipoTel} aoMudar={setTipoTel} /> },
    {
      nome: 'regime',
      rotulo: t('campos.regime'),
      tipo: 'selecao',
      desabilitado: !podeEditar,
      ajuda: !podeEditar ? t('crm.lbEmpresa.regimeSoLeitura') : atual ? t('crm.lbEmpresa.regimeAjuda') : undefined,
      opcoes: REGIMES.map((x) => ({ valor: x, rotulo: t(`valores.regime.${x}`) })),
    },
    { nome: 'observacoes', rotulo: t('crm.campos.resumo'), tipo: 'area', nomes: [atual?.nome] },
    // Endereço (C13; crm5 E2): sempre editável, como no cadastro completo. Na edição, muda o primeiro endereço (ou cria um).
    { nome: 'endereco.cep', rotulo: t('crm.aj.endereco.cep'), tipo: 'cep', largura: 'terco', grupo: grupoEndereco, preencher: preencherCep, ajuda: consultaCep ? t(`consulta.${consultaCep}`) : undefined },
    { nome: 'endereco.logradouro', rotulo: t('crm.aj.endereco.logradouro'), tipo: 'texto', largura: 'meia', grupo: grupoEndereco },
    { nome: 'endereco.numero', rotulo: t('crm.aj.endereco.numero'), tipo: 'texto', largura: 'terco', grupo: grupoEndereco, sensivel: 'oculto' },
    { nome: 'endereco.complemento', rotulo: t('crm.aj.endereco.complemento'), tipo: 'texto', largura: 'meia', grupo: grupoEndereco, sensivel: 'oculto' },
    { nome: 'endereco.bairro', rotulo: t('crm.aj.endereco.bairro'), tipo: 'texto', largura: 'terco', grupo: grupoEndereco },
    { nome: 'endereco.cidade', rotulo: t('crm.aj.endereco.cidade'), tipo: 'texto', largura: 'terco', grupo: grupoEndereco },
    { nome: 'endereco.uf', rotulo: t('crm.aj.endereco.uf'), tipo: 'texto', largura: 'terco', grupo: grupoEndereco },
  ];
  const enderecoInicial = enderecoAtual ? Object.fromEntries(CAMPOS_ENDERECO.map((k) => [`endereco.${k}`, enderecoAtual[k] ?? ''])) : {};
  // `tipo` só para a condição dos campos personalizados (a Data de fundação é da empresa); não vai na gravação.
  const doRegistro = atual
    ? { tipo: 'empresa', documento: atual.documento ? mascaraDocumento(atual.documento, 'cnpj') : '', razao_social: atual.fiscal?.razao_social ?? '', nome: atual.nome, site: atual.site ?? '', email: atual.email ?? '', telefone: atual.telefone ? telefoneNoCampo(atual.telefone) : '', regime: regimeVigente?.regime ?? '', observacoes: atual.observacoes ?? '', extras: atual.extras, ...enderecoInicial }
    : { tipo: 'empresa' };
  const opcoesMembros = [...(podeEditar || resps.includes(RESPONSAVEL_TODOS) ? [{ valor: RESPONSAVEL_TODOS, rotulo: t('crm.lbEmpresa.todos') }] : []), ...(membros.data?.itens ?? []).map((m) => ({ valor: m.id, rotulo: nomePessoa(m.nome) }))];
  // Membro que saiu do workspace segue visível enquanto escolhido (o nome vem do registro).
  for (const r of [...(atual?.responsavel_id ? [{ id: atual.responsavel_id, nome: atual.responsavel_nome ?? '' }] : []), ...(atual?.responsaveis_adicionais ?? [])]) {
    if (!opcoesMembros.some((o) => o.valor === r.id)) opcoesMembros.push({ valor: r.id, rotulo: nomePessoa(r.nome) });
  }
  const blocoSegmento = (
    <SelecaoMultipla
      id={idSeg}
      rotulo={t('crm.campos.segmento')}
      valores={segs}
      aoMudar={setSegs}
      rotuloPrincipal={t('crm.aj.principal')}
      opcoes={(setores.data?.itens ?? []).map((x) => ({ valor: x.id, rotulo: x.nome }))}
      acaoNovo={
        podeEditar && (
          <Botao variante="fantasma" tamanho="sm" icone={<Plus className="size-4" />} onClick={() => setNovoSeg(true)} data-novo-segmento="">
            {t('crm.aj.novoSegmento')}
          </Botao>
        )
      }
    />
  );
  const blocoResponsavel = (
    <SelecaoMultipla id={idResp} rotulo={t('crm.campos.responsavel')} valores={resps} aoMudar={setResps} rotuloPrincipal={t('crm.aj.principal')} opcoes={opcoesMembros} limite={podeEditar ? undefined : 1} ajuda={!podeEditar ? t('crm.aj.umResponsavelAjuda') : resps.includes(RESPONSAVEL_TODOS) ? t('crm.lbEmpresa.todosAjuda') : t('crm.aj.carteiraAjuda')} />
  );
  return (
    <Drawer
      aberto={aberto}
      aoFechar={aoFechar}
      titulo={atual ? t('crm.empresas.editar') : t('crm.empresas.criar')}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.cancelar')}
          </Botao>
          {criarOutra && !atual && (
            <Botao variante="secundario" type="submit" form="form-crm-empresa" data-salvar-e-criar-outra="" onClick={(e) => (outra.current = e.detail > 0 || document.activeElement === e.currentTarget)}>
              {t('crm.empresas.salvarOutra')}
            </Botao>
          )}
          <Botao variante="primario" type="submit" form="form-crm-empresa" onClick={() => (outra.current = false)}>
            {atual ? tn('comum.salvar') : t('crm.empresas.criar')}
          </Botao>
        </>
      }
    >
      {/* Lista de 30/10 (B), itens 24 e 25: a frase da origem com o "Criar novos campos", que abre o pop-up de criar o campo. */}
      <CriarCamposDica entidade="cadastros.parceiro" abaFixa={{ campo: 'tipo', valor: 'empresa', aba: t('crm.empresas.titulo') }} frase={t('crm.aj.dicaCampos')} link={t('crm.aj.criarNovosCampos')} />
      <Formulario
        key={atual?.id ?? 'nova'}
        id="form-crm-empresa"
        campos={campos}
        inicial={doRegistro}
        entidade="cadastros.parceiro"
        extrasOcultos={EXTRAS_FORA_DO_CRM}
        depoisDoCampo={{
          nome: blocoSegmento,
          email: <ListaMeios meio="email" itens={emails} aoMudar={setEmails} erros={errosMeios} pode={podeEditar} />,
          telefone: <ListaMeios meio="telefone" itens={telefones} aoMudar={setTelefones} erros={errosMeios} pode={podeEditar} />,
          observacoes: blocoResponsavel,
        }}
        aoEnviar={async (v) => {
          const endereco = Object.fromEntries(CAMPOS_ENDERECO.map((k) => [k, texto(v[`endereco.${k}`])]));
          const temEndereco = Object.values(endereco).some(Boolean);
          // Na edição o endereço vai antes (a validação dele barra o resto): corrige o primeiro ou cria um. Apagar tudo não
          // apaga o endereço (isso fica no cadastro completo).
          if (atual && temEndereco) {
            // Lido na hora do envio (o ref muda sem novo render): o criado na tentativa anterior conta como o atual.
            const criado = enderecoNovo.current?.criado;
            const enderecoAtual: EnderecoBase | undefined = atual.enderecos?.[0] ?? (criado ? { id: enderecoNovo.current!.id, ...criado } : undefined);
            const mudou = !enderecoAtual || CAMPOS_ENDERECO.some((k) => (enderecoAtual[k] ?? null) !== endereco[k]);
            if (enderecoAtual && mudou) {
              await patch(`/cadastros/parceiros/${atual.id}/enderecos/${enderecoAtual.id}`, endereco);
              if (enderecoNovo.current?.criado) enderecoNovo.current.criado = endereco;
            } else if (!enderecoAtual) {
              const novo = (enderecoNovo.current ??= { id: crypto.randomUUID(), criado: null });
              try {
                await post(`/cadastros/parceiros/${atual.id}/enderecos`, { id: novo.id, tipo: 'principal', ...endereco });
              } catch (err) {
                if (!(err instanceof ErroRequisicao && err.status === 409 && err.corpo.code === 'registro.duplicado')) throw err;
              }
              novo.criado = endereco;
            }
          }
          const responsaveis = corpoDosResponsaveis(resps);
          const documento = texto(v.documento);
          const id = await gravar(atual, {
            ...(atual ? {} : { tipo: 'empresa', e_cliente: true, situacao_comercial: 'lead', origem: 'crm', ...(temEndereco ? { endereco } : {}) }),
            // O CNPJ (Brasil): na criação só quando digitado; na edição, também para apagar.
            ...(documento || atual?.documento ? { documento } : {}),
            nome: v.nome,
            setor_id: segs[0] ?? null,
            site: texto(v.site),
            email: texto(v.email),
            telefone: texto(v.telefone),
            telefone_tipo: texto(v.telefone) ? tipoTel : null,
            observacoes: texto(v.observacoes),
            ...responsaveis.principal,
            extras: v.extras ?? {},
          });
          const adicionais = { setor_ids: segs.slice(1), ...responsaveis.adicionais, telefones: listaDeMeios(telefones), emails: listaDeMeios(emails) };
          const tinha = (atual?.setores_adicionais?.length ?? 0) + (atual?.responsaveis_adicionais?.length ?? 0) + (atual?.responsavel_todos ? 1 : 0) + (atual?.meios?.length ?? 0);
          if (podeEditar && (tinha || adicionais.setor_ids.length || adicionais.responsavel_ids.length || adicionais.responsavel_todos || adicionais.telefones.length || adicionais.emails.length)) await gravarAdicionais(id, adicionais, setErrosMeios, !atual, toast.erro);
          // Dados fiscais da Receita (consultados nesta abertura) e o regime: depois do cadastro, sem travá-lo (a falha avisa).
          if (podeEditar) {
            try {
              // Lista de 02/11 (item 3): a razão social e o Nome Fantasia vão aos dados fiscais (com o que a Receita trouxe, na criação).
              const fiscal = corpoFiscalDaEmpresa({ fiscal: atual?.fiscal, nomeAtual: atual?.nome ?? null, razao: texto(v.razao_social), nome: String(v.nome).trim(), receita: !atual?.fiscal ? (daReceita.current.fiscal as Partial<Fiscal> | null) : null });
              if (fiscal) await api('PUT', `/cadastros/parceiros/${id}/fiscal`, fiscal);
              const regime = texto(v.regime);
              if (regime && regime !== (regimeVigente?.regime ?? null)) await gravarRegime(id, regime, regimeVigente, daReceita.current.regime);
            } catch (e) {
              if (atual) throw e;
              toast.erro(mensagemDeErro(e));
            }
          }
          await depois();
          if (outra.current && !atual) return aoRecomecar();
          aoFechar();
          aoSalvar?.(id);
        }}
      />
      <NovoSegmento aberto={novoSeg} aoFechar={() => setNovoSeg(false)} aoCriar={(id) => setSegs((x) => [...x, id])} />
    </Drawer>
  );
}

/**
 * Lista de 30/10 (B), item 3.1 (d): grava o regime escolhido como período novo. Começa hoje (no fuso de São Paulo) ou, se é o
 * que a Receita informou, na data da opção. O período em vigor que começa hoje ou depois é correção do mesmo dia: sai antes
 * (senão o servidor recusaria a sobreposição); o anterior fecha sozinho no dia anterior ao novo.
 */
async function gravarRegime(id: string, regime: string, vigente: { id: string; regime: string; inicio: string } | null, receita: { regime: string; inicio: string } | null) {
  const hoje = hojeNoFuso('America/Sao_Paulo');
  const inicio = !vigente && receita?.regime === regime && receita.inicio <= hoje ? receita.inicio : hoje;
  if (vigente && vigente.inicio.slice(0, 10) >= inicio) await del(`/cadastros/parceiros/${id}/regimes/${vigente.id}`);
  await post(`/cadastros/parceiros/${id}/regimes`, { id: crypto.randomUUID(), regime, inicio });
}

/**
 * Onde os campos personalizados do contato entram (como no RD, lista de 02/11 item 1): usuário do WhatsApp depois do Sexo; LinkedIn
 * depois da empresa; aniversário depois de "Contato e envio de comunicação". O Responsável vem depois do aniversário (ou da base
 * legal, se o workspace não tem o campo) e os demais campos personalizados, depois dele. O WhatsApp (telefone) sumiu: é tipo de telefone.
 */
const POSICAO_EXTRAS_CONTATO = { whatsapp_usuario: { depoisDe: 'sexo', prefixo: '@', largura: 'inteira' }, linkedin: { depoisDe: 'parent_id', largura: 'inteira' }, data_aniversario: { depoisDe: 'base_legal', largura: 'inteira' } } as const;

type PropsFormContato = { aberto: boolean; aoFechar: () => void; atual?: ParceiroCrm | null; empresa?: { id: string; nome: string }; aoSalvar?: (id: string) => void; criarOutra?: boolean };

/** Como o `FormEmpresa` (ROB-M1): o estado do formulário recomeça a cada abertura, a partir do `atual` vigente. */
export function FormContato(props: PropsFormContato) {
  const [n, setN] = useState(0);
  return props.aberto ? <FormContatoAberto key={n} {...props} aoRecomecar={() => setN((x) => x + 1)} /> : null;
}

function FormContatoAberto({ aberto, aoFechar, atual, empresa, aoSalvar, criarOutra, aoRecomecar }: PropsFormContato & { aoRecomecar: () => void }) {
  const outra = useRef(false);
  const { t } = useTranslation('cadastros');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const depois = useDepoisDeSalvar();
  const toast = useToast();
  const membros = useMembros();
  const extrasDefs = useCamposPersonalizados('cadastros.parceiro');
  const idResp = useId();
  const podeEditar = s.pode('cadastros.editar');
  // Responsáveis (vários, como na empresa): na criação, a própria pessoa. O "Todos" só aparece para quem já o tem no registro.
  const inicialResp = atual ? responsaveisDoRegistro(atual) : [s.usuario.id];
  const [tipoTel, setTipoTel] = useState<TipoMeio>(() => tipoDoTelefone(atual));
  const [telefones, setTelefones] = useState<Meio[]>(() => meiosDe(atual, 'telefone'));
  const [emails, setEmails] = useState<Meio[]>(() => meiosDe(atual, 'email'));
  const [resps, setResps] = useState<string[]>(inicialResp);
  const [errosMeios, setErrosMeios] = useState<Record<string, string>>({});
  const [chave, setChave] = useState(atual?.id ?? '');
  if ((atual?.id ?? '') !== chave) {
    setChave(atual?.id ?? '');
    setTipoTel(tipoDoTelefone(atual));
    setTelefones(meiosDe(atual, 'telefone'));
    setEmails(meiosDe(atual, 'email'));
    setResps(inicialResp);
    setErrosMeios({});
  }
  const campos: DefCampo[] = [
    { nome: 'nome', rotulo: t('crm.campos.nomeContato'), tipo: 'texto', sensivel: 'nome', obrigatorio: true, placeholder: t('crm.aj.nomeContatoPlaceholder') },
    { nome: 'cargo', rotulo: t('crm.campos.cargo'), tipo: 'texto', nomes: [atual?.nome], placeholder: t('crm.aj.cargoPlaceholder') },
    // Lista de 02/11 (item 1): o Sexo no lugar do Tratamento. O servidor grava o tratamento derivado (Masculino: Sr.; Feminino: Sra.;
    // Não informado: neutro) para as etiquetas dos modelos de e-mail. Dado pessoal: some no modo discreto.
    { nome: 'sexo', rotulo: t('crm.campos.sexo'), tipo: 'selecao', sensivel: 'oculto', ajuda: t('crm.campos.sexoAjuda'), opcoes: SEXOS.map((x) => ({ valor: x, rotulo: t(`crm.sexo.${x}`) })) },
    // O tipo do telefone principal ao lado do número (Comercial por padrão, como no RD).
    { nome: 'telefone', rotulo: t('crm.campos.telefone'), tipo: 'telefone', transformar: mascaraTelefoneDigitando, ladoEsquerdo: <TipoDoTelefone valor={tipoTel} aoMudar={setTipoTel} /> },
    { nome: 'email', rotulo: t('crm.campos.email'), tipo: 'email', placeholder: 'seunome@email.com' },
    { nome: 'parent_id', rotulo: t('crm.campos.empresaDoContato'), tipo: 'busca', busca: { caminho: '/cadastros/parceiros', params: { papel: 'empresa' }, rotulo: (i) => String(i.nome), pessoa: true } },
    { nome: 'base_legal', rotulo: t('crm.campos.baseLegal'), tipo: 'selecao', opcoes: BASES_LEGAIS.map((b) => ({ valor: b, rotulo: t(`crm.baseLegal.${b}`) })) },
  ];
  // `tipo` só para a condição dos campos personalizados (aniversário e usuário do WhatsApp são da pessoa); não vai na gravação.
  const inicial = atual
    ? { tipo: 'pessoa', nome: atual.nome, cargo: atual.cargo ?? '', sexo: atual.sexo ?? '', telefone: atual.telefone ? telefoneNoCampo(atual.telefone) : '', email: atual.email ?? '', parent_id: atual.parent_id, base_legal: atual.base_legal ?? '', extras: atual.extras }
    : { tipo: 'pessoa', parent_id: empresa?.id ?? null, base_legal: 'nao_sabe' };
  const rotulos: Record<string, string> = {};
  const empresaNome = atual?.parent_nome ?? empresa?.nome;
  if (empresaNome) rotulos.parent_id = nomePessoa(empresaNome);
  const opcoesMembros = [...(resps.includes(RESPONSAVEL_TODOS) ? [{ valor: RESPONSAVEL_TODOS, rotulo: t('crm.lbEmpresa.todos') }] : []), ...(membros.data?.itens ?? []).map((m) => ({ valor: m.id, rotulo: nomePessoa(m.nome) }))];
  // Membro que saiu do workspace segue visível enquanto escolhido (o nome vem do registro).
  for (const r of [...(atual?.responsavel_id ? [{ id: atual.responsavel_id, nome: atual.responsavel_nome ?? '' }] : []), ...(atual?.responsaveis_adicionais ?? [])]) {
    if (!opcoesMembros.some((o) => o.valor === r.id)) opcoesMembros.push({ valor: r.id, rotulo: nomePessoa(r.nome) });
  }
  if (!atual && !opcoesMembros.some((o) => o.valor === s.usuario.id)) opcoesMembros.push({ valor: s.usuario.id, rotulo: nomePessoa(s.usuario.nome) });
  const blocoResponsavel = (
    <SelecaoMultipla id={idResp} rotulo={t('crm.campos.responsavel')} valores={resps} aoMudar={setResps} rotuloPrincipal={t('crm.aj.principal')} opcoes={opcoesMembros} limite={podeEditar ? undefined : 1} ajuda={!podeEditar ? t('crm.aj.umResponsavelAjuda') : resps.includes(RESPONSAVEL_TODOS) ? t('crm.lbEmpresa.todosAjuda') : t('crm.aj.carteiraAjuda')} />
  );
  // O Responsável vem logo depois do aniversário (o último campo do RD); sem o campo no workspace, depois da base legal.
  const depoisDe = extrasDefs.some((d) => d.nome === 'extras.data_aniversario') ? 'extras.data_aniversario' : 'base_legal';
  return (
    <Drawer
      aberto={aberto}
      aoFechar={aoFechar}
      titulo={atual ? t('crm.contatos.editar') : t('crm.contatos.criar')}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.cancelar')}
          </Botao>
          {criarOutra && !atual && (
            <Botao variante="secundario" type="submit" form="form-crm-contato" data-salvar-e-criar-outra="" onClick={(e) => (outra.current = e.detail > 0 || document.activeElement === e.currentTarget)}>
              {t('crm.contatos.salvarOutro')}
            </Botao>
          )}
          <Botao variante="primario" type="submit" form="form-crm-contato" onClick={() => (outra.current = false)}>
            {atual ? tn('comum.salvar') : t('crm.contatos.criar')}
          </Botao>
        </>
      }
    >
      <CriarCamposDica entidade="cadastros.parceiro" abaFixa={{ campo: 'tipo', valor: 'pessoa', aba: t('crm.contatos.titulo') }} frase={t('crm.aj.dicaCamposContato')} link={t('crm.aj.criarNovosCampos')} />
      <Formulario
        key={atual?.id ?? `novo-${empresa?.id ?? ''}`}
        id="form-crm-contato"
        campos={campos}
        inicial={inicial}
        entidade="cadastros.parceiro"
        rotulosAtuais={rotulos}
        posicaoExtras={POSICAO_EXTRAS_CONTATO}
        extrasOcultos={EXTRAS_FORA_DO_CRM}
        depoisDoCampo={{
          telefone: <ListaMeios meio="telefone" itens={telefones} aoMudar={setTelefones} erros={errosMeios} pode={podeEditar} />,
          email: <ListaMeios meio="email" itens={emails} aoMudar={setEmails} erros={errosMeios} pode={podeEditar} />,
          [depoisDe]: blocoResponsavel,
        }}
        aoEnviar={async (v) => {
          const extras = { ...((v.extras as Record<string, unknown>) ?? {}) };
          // O usuário do WhatsApp grava sem o "@" (a ficha mostra com ele), como no RD.
          if (typeof extras.whatsapp_usuario === 'string') extras.whatsapp_usuario = extras.whatsapp_usuario.trim().replace(/^@+/, '') || null;
          const responsaveis = corpoDosResponsaveis(resps);
          const id = await gravar(atual, {
            ...(atual ? {} : { tipo: 'pessoa', e_cliente: true, situacao_comercial: 'lead', origem: 'crm' }),
            nome: v.nome,
            cargo: texto(v.cargo),
            // O servidor grava o tratamento derivado do sexo (as etiquetas dos e-mails seguem certas).
            sexo: texto(v.sexo),
            telefone: texto(v.telefone),
            telefone_tipo: texto(v.telefone) ? tipoTel : null,
            email: texto(v.email),
            parent_id: texto(v.parent_id),
            base_legal: texto(v.base_legal),
            // O principal vai no cadastro; os outros, nos adicionais (com `cadastros.editar`), como na empresa.
            ...responsaveis.principal,
            extras,
          });
          const adicionais = { telefones: listaDeMeios(telefones), emails: listaDeMeios(emails), ...responsaveis.adicionais };
          const tinha = (atual?.meios ?? []).length + (atual?.responsaveis_adicionais?.length ?? 0) + (atual?.responsavel_todos ? 1 : 0);
          if (podeEditar && (tinha || adicionais.telefones.length || adicionais.emails.length || adicionais.responsavel_ids.length || adicionais.responsavel_todos)) await gravarAdicionais(id, adicionais, setErrosMeios, !atual, toast.erro);
          await depois();
          if (outra.current && !atual) return aoRecomecar();
          aoFechar();
          aoSalvar?.(id);
        }}
      />
    </Drawer>
  );
}
